import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AppShell, useShell } from '../components/AppShell';
import { SmsHygieneMeter } from '../components/SmsHygieneMeter';
import { Mail, MessageSquare, RotateCcw, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simple mustache-lite renderer for the preview panel — replaces
// {{token}} with a friendly sample value so shop admins can see what
// customers will actually receive.
const SAMPLES = {
  customer_name: 'Alex Rivera',
  customer_first_name: 'Alex',
  shop_name: 'Sharp & Sons',
  shop_sms_name: 'Sharp & Sons',
  barber_name: 'Marcus Bell',
  barber_first_name: 'Marcus',
  service_name: 'Cut & Beard Trim',
  starts_at_date: 'Wed 23 Jul',
  starts_at_time: '10:30',
  cancel_url: 'https://instacut.co.uk/cancel/abc123',
  short_cancel_url: 'https://instacut.co.uk/c/aB3d9F',
  diary_url: 'https://instacut.co.uk/diary',
  customer_phone: '07700 900123',
};
const renderPreview = (s) =>
  String(s || '').replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (_, k) => SAMPLES[k] ?? `{{${k}}}`);

const TemplatesInner = () => {
  const { profile } = useShell();
  const [rows, setRows] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // (kind, channel) key
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [channelFilter, setChannelFilter] = useState('all'); // all | email | sms

  const filteredRows = useMemo(
    () => rows.filter(r => channelFilter === 'all' || r.channel === channelFilter),
    [rows, channelFilter],
  );

  const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };
  };

  const load = async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${BACKEND_URL}/api/templates`, { headers });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Failed to load');
      setRows(j.templates || []);
      setCanEdit(!!j.shop_can_edit);
      if (!selected && j.templates?.length) {
        setSelected(`${j.templates[0].kind}|${j.templates[0].channel}`);
      }
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { if (profile) load(); }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = useMemo(() => {
    if (!selected) return null;
    const [k, c] = selected.split('|');
    return rows.find(r => r.kind === k && r.channel === c) || null;
  }, [rows, selected]);

  useEffect(() => {
    if (current) setDraft({ subject: current.subject || '', body: current.body || '', enabled: current.enabled, allow_unicode: false });
  }, [current]);

  const dirty = draft && current && (
    (draft.subject || '') !== (current.subject || '') ||
    (draft.body    || '') !== (current.body    || '') ||
    draft.enabled  !== current.enabled
  );

  const save = async () => {
    if (!current || !draft) return;
    setSaving(true); setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${BACKEND_URL}/api/templates`, {
        method: 'POST', headers,
        body: JSON.stringify({
          kind: current.kind, channel: current.channel,
          subject: current.has_subject ? draft.subject : null,
          body: draft.body, enabled: draft.enabled,
          allow_unicode: !!draft.allow_unicode,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Save failed');
      await load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  // Standard-plan toggle — flips only `enabled` without touching copy.
  const toggleEnabled = async (next) => {
    if (!current) return;
    // Optimistic — flip the draft immediately so the checkbox doesn't
    // lag behind the click.
    setDraft(d => ({ ...(d || {}), enabled: next }));
    setSaving(true); setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${BACKEND_URL}/api/templates/enabled`, {
        method: 'POST', headers,
        body: JSON.stringify({
          kind: current.kind, channel: current.channel, enabled: next,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Toggle failed');
      await load();
    } catch (e) {
      setError(e.message);
      // Revert on failure
      setDraft(d => ({ ...(d || {}), enabled: !next }));
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!current) return;
    if (!window.confirm('Reset this template to the platform default?')) return;
    setSaving(true); setError('');
    try {
      const headers = await authHeaders();
      await fetch(`${BACKEND_URL}/api/templates/${current.kind}/${current.channel}`, {
        method: 'DELETE', headers,
      });
      await load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2>Message templates</h2>
          <div className="sub">
            Customer and barber notifications sent by SMS + email.
            {!canEdit && (
              <span style={{ color: 'var(--acc)', marginLeft: 8 }}>
                <Lock size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                Copy edits are a Premium feature — Standard shops use platform defaults, but you can still toggle each notification on or off below.
                <Link
                  to="/billing"
                  className="btn s"
                  data-testid="tpl-upgrade-link"
                  style={{
                    marginLeft: 10,
                    padding: '4px 10px',
                    fontSize: 12,
                    background: 'var(--acc)',
                    color: '#fff',
                    borderColor: 'var(--acc)',
                  }}
                >Upgrade →</Link>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="body" data-testid="templates-page">
        {loading ? null : (
          <div className="templates-layout">
            {/* Sidebar list */}
            <div className="templates-list">
              {/* Channel filter tabs */}
              <div style={{ display: 'flex', gap: 4, padding: '8px 8px 4px' }} data-testid="templates-filter">
                {['all', 'email', 'sms'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setChannelFilter(t)}
                    data-testid={`templates-filter-${t}`}
                    style={{
                      flex: 1,
                      padding: '4px 6px',
                      fontSize: 11,
                      fontWeight: 600,
                      border: 0,
                      borderRadius: 6,
                      cursor: 'pointer',
                      background: channelFilter === t ? 'var(--acc, #6366f1)' : 'transparent',
                      color: channelFilter === t ? '#fff' : 'var(--text-mute)',
                      textTransform: 'uppercase',
                    }}
                  >{t}</button>
                ))}
              </div>
              {filteredRows.map(r => {
                const key = `${r.kind}|${r.channel}`;
                const active = key === selected;
                const Icon = r.channel === 'email' ? Mail : MessageSquare;
                return (
                  <button
                    key={key}
                    className={`templates-item${active ? ' is-active' : ''}`}
                    onClick={() => setSelected(key)}
                    data-testid={`tpl-item-${r.kind}-${r.channel}`}
                  >
                    <Icon size={14} style={{ flexShrink: 0, marginTop: 2, color: r.channel === 'email' ? 'var(--acc)' : '#059669' }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="ti-title" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {r.kind.replace(/_/g, ' ')}
                        <span className="ti-channel">{r.channel.toUpperCase()}</span>
                        <PlanPill plan={r.plan} />
                      </div>
                      <div className="ti-desc">{r.description}</div>
                    </div>
                    {r.is_override && <span className="ti-mark" title="Custom template" />}
                  </button>
                );
              })}
            </div>

            {/* Editor + preview */}
            {current && draft && (() => {
              // SMS is read-only for every shop (platform-controlled to
              // protect GSM-7 compliance + PECR STOP disclosure). Copy
              // edits only ever land on the email channel.
              const bodyEditable = canEdit && current.channel !== 'sms';
              return (
              <div className="templates-editor">
                <div className="templates-meta">
                  <div>
                    <div className="tm-kind" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {current.kind.replace(/_/g, ' ')}
                      <PlanPill plan={current.plan} />
                    </div>
                    <div className="tm-desc">{current.description}</div>
                  </div>
                  {current.is_override && bodyEditable && (
                    <button className="btn s" onClick={resetToDefault} disabled={saving} data-testid="tpl-reset">
                      <RotateCcw size={12} /> Reset to default
                    </button>
                  )}
                </div>

                {error && <div className="error-message" data-testid="tpl-error">{error}</div>}

                {current.channel === 'sms' && (
                  <div
                    data-testid="tpl-sms-readonly-notice"
                    style={{
                      padding: '10px 12px', borderRadius: 8,
                      background: '#eff6ff', border: '1px solid #bfdbfe',
                      color: '#1e40af', fontSize: 12, marginBottom: 10,
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                    }}
                  >
                    <Lock size={14} style={{ marginTop: 1, flexShrink: 0 }} />
                    <div>
                      SMS copy is managed by Instacut to keep sends GSM-7 compliant and
                      within UK compliance rules. You can still switch this notification
                      on or off below.
                    </div>
                  </div>
                )}

                {current.has_subject && (
                  <div className="field">
                    <label>Subject</label>
                    <input
                      className="inp"
                      value={draft.subject}
                      onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                      disabled={!bodyEditable || saving}
                      data-testid="tpl-subject"
                    />
                  </div>
                )}

                <div className="field">
                  <label>{current.channel === 'email' ? 'Body (markdown)' : 'SMS body'}</label>
                  <textarea
                    className="inp"
                    rows={current.channel === 'email' ? 10 : 4}
                    value={draft.body}
                    onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                    disabled={!bodyEditable || saving}
                    data-testid="tpl-body"
                  />
                  <div className="tm-tokens">
                    Available tokens:
                    {current.tokens.map(t => (
                      <button
                        key={t}
                        type="button"
                        className="token-chip"
                        disabled={!bodyEditable || saving}
                        onClick={() => {
                          if (!bodyEditable) return;
                          setDraft(d => ({ ...d, body: (d.body || '') + `{{${t}}}` }));
                        }}
                        data-testid={`tpl-token-${t}`}
                      >{`{{${t}}}`}</button>
                    ))}
                  </div>
                  {current.channel === 'sms' && (
                    <SmsHygieneMeter
                      body={draft.body}
                      onNormalisedChange={null}
                      allowUnicode={false}
                      onAllowUnicodeChange={null}
                    />
                  )}
                </div>

                {/* Enable toggle — available on every template, both
                    plans. Standard shops use this to silence without
                    editing; Premium keeps the same checkbox with copy
                    control below (email only). */}
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--mute)', marginTop: 6 }}
                  data-testid="tpl-enabled-row"
                >
                  <input
                    type="checkbox"
                    checked={!!draft.enabled}
                    disabled={saving}
                    onChange={(e) => bodyEditable
                      ? setDraft({ ...draft, enabled: e.target.checked })
                      : toggleEnabled(e.target.checked)}
                    data-testid="tpl-enabled"
                  />
                  Send this notification
                </label>

                {bodyEditable && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button className="btn p" onClick={save} disabled={!dirty || saving} data-testid="tpl-save">
                      {saving ? 'Saving…' : 'Save template'}
                    </button>
                    {dirty && (
                      <button className="btn s" onClick={() => setDraft({
                        subject: current.subject || '',
                        body: current.body || '',
                        enabled: current.enabled,
                      })} disabled={saving} data-testid="tpl-discard">Discard changes</button>
                    )}
                  </div>
                )}

                <div className="templates-preview" data-testid="tpl-preview">
                  <div className="tp-head">Preview with sample data</div>
                  {current.has_subject && (
                    <div className="tp-subject">{renderPreview(draft.subject)}</div>
                  )}
                  <div className="tp-body">
                    {renderPreview(draft.body).split('\n').map((line, i) => (
                      <p key={i}>{line || <br />}</p>
                    ))}
                  </div>
                </div>
              </div>
              );
            })()}
          </div>
        )}
      </div>
    </main>
  );
};

const PlanPill = ({ plan }) => {
  const meta = plan === 'premium'
    ? { bg: '#7c3aed', label: 'PREMIUM', title: 'Premium plans only' }
    : plan === 'system'
      ? { bg: '#0891b2', label: 'SYSTEM', title: 'System notification' }
      : { bg: '#6b7280', label: 'ALL PLANS', title: 'All plans' };
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 700,
        padding: '1px 6px',
        borderRadius: 4,
        letterSpacing: 0.4,
        background: meta.bg,
        color: '#fff',
      }}
      title={meta.title}
    >
      {meta.label}
    </span>
  );
};

const Templates = () => <AppShell><TemplatesInner /></AppShell>;
export default Templates;
